import {
  Box,
  Card,
  Chip,
  LinearProgress,
  Skeleton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { KeyboardArrowRight } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveIcon from "@mui/icons-material/Remove";
import makeStyles from "@mui/styles/makeStyles";

import { Table } from "@APP/components";
import { tableStyles } from "@APP/styles";
import { ReusableRTP } from "@APP/types";
import { formatCurrency, formatUtcDate } from "@APP/utils";

import { accentFor } from "./statusAccent";

type Props = {
  data: ReusableRTP[];
  loading: boolean;
  loaded: boolean;
  page: number;
  entries: number;
  lastPage: number;
  onPageChange: (page: number) => void;
  onEntriesChange: (entries: number) => void;
  onView: (rtp: ReusableRTP) => void;
};

const HEADERS = [
  "Username",
  "Description",
  "Reference",
  "Requested",
  "Received",
  "Links",
  "Expiry",
  "Name req.",
  "Tips",
  "Status",
];

// header columns + the trailing chevron action column
const COL_SPAN = HEADERS.length + 1;

const useStyles = makeStyles((theme) => ({
  card: {
    overflowX: "auto",
  },
  row: {
    cursor: "pointer",
    transition: "background-color 120ms ease",
    "&:hover": {
      backgroundColor: "rgba(0, 32, 92, 0.03)",
    },
  },
  username: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    whiteSpace: "nowrap",
  },
  description: {
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  reference: {
    fontFamily: "'Roboto Mono', ui-monospace, monospace",
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  receivedCell: {
    minWidth: 150,
  },
  received: {
    fontWeight: 700,
    color: "#00B189",
    fontVariantNumeric: "tabular-nums",
  },
  bar: {
    height: 6,
    borderRadius: 999,
    marginTop: 4,
    backgroundColor: "#E6F7F2",
    "& .MuiLinearProgress-bar": {
      borderRadius: 999,
      backgroundColor: "#00B189",
    },
  },
  openTag: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  nowrap: {
    whiteSpace: "nowrap",
  },
  statusPill: {
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    height: 22,
  },
  flag: {
    display: "inline-flex",
  },
  loading: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

const ReusableRTPList = ({
  data,
  loading,
  loaded,
  page,
  entries,
  lastPage,
  onPageChange,
  onEntriesChange,
  onView,
}: Props) => {
  const classes = useStyles();
  const tableClasses = tableStyles();

  if (loading) {
    return (
      <Card elevation={2}>
        <Box className={classes.loading}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={44} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
      </Card>
    );
  }

  const renderHeader = () => (
    <TableRow className={tableClasses.tableHead}>
      {HEADERS.map((label) => (
        <TableCell key={label}>
          <Typography variant="caption" fontWeight={700} color="textSecondary">
            {label}
          </Typography>
        </TableCell>
      ))}
      <TableCell padding="checkbox" />
    </TableRow>
  );

  const renderRows = (rtp: ReusableRTP) => {
    const accent = accentFor(rtp.status);
    const received = Number(rtp.totalAmountReceived?.amount ?? 0);
    const requested = rtp.amountRequested ? Number(rtp.amountRequested.amount) : null;
    const currency = rtp.totalAmountReceived?.currency ?? "GBP";
    const percent = requested && requested > 0 ? Math.min(100, (received / requested) * 100) : null;
    const expiry = formatUtcDate(rtp.expiresAt);

    const flag = (on: boolean) =>
      on ? (
        <CheckCircleIcon fontSize="small" sx={{ color: "#00B189" }} className={classes.flag} />
      ) : (
        <RemoveIcon fontSize="small" className={`${classes.muted} ${classes.flag}`} />
      );

    return (
      <TableRow
        key={rtp.id}
        hover
        className={classes.row}
        onClick={() => onView(rtp)}
        role="button"
        aria-label={`View details for ${rtp.description}`}>
        <TableCell>
          <Typography variant="body2" className={classes.username} title={rtp.username}>
            {rtp.username}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" className={classes.description} title={rtp.description}>
            {rtp.description}
          </Typography>
        </TableCell>
        <TableCell>
          <span className={classes.reference}>{rtp.reference}</span>
        </TableCell>
        <TableCell className={classes.nowrap}>
          {requested !== null ? (
            formatCurrency(rtp.amountRequested!.amount, currency)
          ) : (
            <span className={classes.openTag}>Open</span>
          )}
        </TableCell>
        <TableCell className={classes.receivedCell}>
          <Typography variant="body2" className={classes.received}>
            {formatCurrency(String(received), currency)}
          </Typography>
          {percent !== null ? (
            <LinearProgress variant="determinate" value={percent} className={classes.bar} />
          ) : (
            <span className={classes.openTag}>open amount</span>
          )}
        </TableCell>
        <TableCell>{rtp.paymentLinksUsed}</TableCell>
        <TableCell className={`${classes.nowrap} ${expiry ? "" : classes.muted}`}>
          {expiry ?? "—"}
        </TableCell>
        <TableCell align="center">{flag(rtp.requireCustomerName)}</TableCell>
        <TableCell align="center">{flag(rtp.allowTip)}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={rtp.status}
            className={classes.statusPill}
            style={{ color: accent, backgroundColor: `${accent}1A` }}
          />
        </TableCell>
        <TableCell padding="checkbox">
          <KeyboardArrowRight fontSize="small" className={classes.muted} />
        </TableCell>
      </TableRow>
    );
  };

  const renderEmptyDataRow = () => (
    <TableRow>
      <TableCell colSpan={COL_SPAN} align="center" sx={{ py: 6 }}>
        <Typography variant="body2" color="textSecondary">
          {loaded
            ? "No reusable RTPs match this filter. Try widening the date range or a different search."
            : ""}
        </Typography>
      </TableCell>
    </TableRow>
  );

  return (
    <Card elevation={2} className={classes.card}>
      <Table
        data={data}
        renderHeader={renderHeader}
        renderRows={renderRows}
        renderEmptyDataRow={renderEmptyDataRow}
        showPagination={!!data.length}
        entries={entries}
        page={page}
        lastPage={lastPage}
        onPageChange={onPageChange}
        onEntriesChange={onEntriesChange}
      />
    </Card>
  );
};

export default ReusableRTPList;
